import { toast } from "sonner";
import { setMessage } from "../../store/slices/message";
import axiosInstance from "../../utility/axiosInstance";
import {
  AdminApiResponse,
  AdminEdit,
  ApiResponse,
  FeaturesResponse,
  GetAdminsResponse,
  iRegister2,
  OrgSuperAdmin,
  PaginatedResponse,
  PrepareManualTransferRequest,
  PrepareManualTransferResponse,
  RegisterAdmin,
  RenewFeaturesResponse,
  SubmitPaymentReceiptResponse,
  SuperAdminResponse,
  ConsoleAddonsResponse,
  ConsolePaymentRequest,
  OfferingsResponse,
} from "../../store/types/settings";
import axios, { isAxiosError } from "axios";
import { setLoading, stopLoading } from "../../store/slices/loading";
import { useEffect, useState } from "react";
import { SubscriptionResponse } from "../../store/types/settings";

// Register
type registerType = (
  dispatch: any,
  formData: RegisterAdmin,
  // id?: string
) => Promise<void>;
export const registerAdmin: registerType = async (dispatch, formData) => {
  dispatch(setLoading());
  try {
    // Ensure organizationId is included in the request body
    const requestData = {
      ...formData,
      // organizationId: id || formData.organizationId, // Use id parameter or fallback to formData.organizationId
    };

    const response = await axiosInstance.post(
      `/organizations/admins`,
      requestData,
    );

    if (response.data?.message === "This account already exist!") {
      throw new Error(response.data?.message);
    } else {
      toast.success(response?.data?.message);
      dispatch(
        setMessage({
          type: "success",
          status: "success",
          message: response?.data?.message,
          title: "Admin Created Successful",
        }),
      );
    }
  } catch (error) {
    if (isAxiosError(error)) {
      toast.error(error?.response?.data?.message);
      dispatch(
        setMessage({
          type: "error",
          status: "error",
          message: error?.response?.data?.message,
          title: "Failed",
        }),
      );
    } else {
    }
  }
  dispatch(stopLoading());
};

// Update Admin
export const updateAdmin = async (
  dispatch: any,
  id: string,
  adminData: AdminEdit,
) => {
  try {
    dispatch(setLoading());
    console.log("This is admin data", adminData);
    const response = await axiosInstance.put(`/admins/${id}`, adminData);

    if (response.data.message) {
      toast.success(response.data.message);
    }

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to update profile";
      toast.error(errorMessage);
    }
    throw error;
  } finally {
    dispatch(stopLoading());
  }
};

export interface UseOrganizationDataReturn {
  organizationData: iRegister2 | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  adminId: string | null;
}

// Custom hook for fetching organization data
export const useOrganizationData = (): UseOrganizationDataReturn => {
  const [organizationData, setOrganizationData] = useState<iRegister2 | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [adminId, setAdminId] = useState<string | null>(null);

  const getAdminIdFromStorage = (): string | null => {
    try {
      const loginData = localStorage.getItem("loginData");
      if (loginData) {
        const parsedData = JSON.parse(loginData);
        return parsedData.data?.admin?.organization?.id || null;
      }
      return null;
    } catch (err) {
      console.error("Error parsing loginData from localStorage:", err);
      return null;
    }
  };

  const fetchOrganizationData = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const orgId = getAdminIdFromStorage();
      setAdminId(orgId);

      if (!orgId) {
        throw new Error("Organization ID not found in localStorage");
      }

      console.log("Fetching organization data with ID:", orgId);

      const response = await axiosInstance.get<ApiResponse | iRegister2>(
        `/organizations/${orgId}`,
      );

      console.log("Full API response:", response.data);

      // Handle different response structures
      let organizationInfo: iRegister2;

      if ("data" in response.data) {
        organizationInfo = (response.data as ApiResponse).data;
      } else {
        organizationInfo = response.data as iRegister2;
      }

      setOrganizationData(organizationInfo);
      console.log("Organization info loaded:", organizationInfo);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to load organization info";
      setError(errorMessage);
      console.error("Error loading organization info:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizationData();
  }, []);

  return {
    organizationData,
    loading,
    error,
    refetch: fetchOrganizationData,
    adminId,
  };
};

// Features service
export const featuresService = {
  // Get features for a specific organization
  getAll: async (organizationId: string): Promise<FeaturesResponse> => {
    try {
      const response = await axiosInstance.get<FeaturesResponse>(
        `/features/organization/${organizationId}`,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(
          error?.response?.data?.message || "Failed to fetch features",
        );
      }
      throw new Error("An unexpected error occurred while fetching features");
    }
  },

  // Get all available features for a specific organization
  getAllAvailable: async (
    organizationId: string,
  ): Promise<FeaturesResponse> => {
    try {
      const response = await axiosInstance.get<FeaturesResponse>(
        `/features/organization/${organizationId}`,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(
          error?.response?.data?.message ||
            "Failed to fetch available features",
        );
      }
      throw new Error(
        "An unexpected error occurred while fetching available features",
      );
    }
  },

  // Renew features for an organization
  renew: async (
    featureIds: string[],
    organizationId: string,
  ): Promise<RenewFeaturesResponse> => {
    try {
      const response = await axiosInstance.post<RenewFeaturesResponse>(
        "/features/renew",
        { featureIds, organizationId },
      );

      if (response.data?.message) {
        toast.success(response.data.message);
      }

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(
          error?.response?.data?.message || "Failed to renew features",
        );
      }
      throw new Error("An unexpected error occurred while renewing features");
    }
  },

  getDirectFeatures: async (): Promise<{
    status: string;
    message: string;
    data: any[];
    error: any;
  }> => {
    try {
      const response = await axiosInstance.get("/billing/direct-features");
      return response.data;
    } catch (error) {
      console.error("Error fetching direct features:", error);
      // Fallback to empty array or throw error handled by caller
      return { status: "error", message: "Failed", data: [], error };
    }
  },
};

// Console service
export const consoleService = {
  getAll: async (): Promise<ConsoleAddonsResponse> => {
    try {
      const response =
        await axiosInstance.get<ConsoleAddonsResponse>("/console/all");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(
          error?.response?.data?.message || "Failed to fetch console add-ons",
        );
      }
      throw new Error(
        "An unexpected error occurred while fetching console add-ons",
      );
    }
  },

  prepareManualTransfer: async (
    requestData: any,
  ): Promise<PrepareManualTransferResponse> => {
    try {
      const response = await axiosInstance.post<PrepareManualTransferResponse>(
        "/console/prepare-manual-transfer",
        requestData,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(
          error?.response?.data?.message ||
            "Failed to prepare console transfer",
        );
      }
      throw new Error(
        "An unexpected error occurred while preparing console transfer",
      );
    }
  },

  submitReceipt: async (
    receipt: File,
    organizationId: string,
    planId: string,
    frequency: string,
    reference?: string,
  ): Promise<SubmitPaymentReceiptResponse> => {
    try {
      const formData = new FormData();
      formData.append("receipt", receipt);
      formData.append("organizationId", organizationId);
      formData.append("planId", planId);
      formData.append("frequency", frequency);
      if (reference) formData.append("reference", reference);

      const response = await axiosInstance.post<SubmitPaymentReceiptResponse>(
        "/console/submit-payment-receipt",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      toast.success(response?.data?.message || "Payment receipt submitted");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(
          error?.response?.data?.message || "Failed to submit payment receipt",
        );
      }
      throw new Error(
        "An unexpected error occurred while submitting payment receipt",
      );
    }
  },
};

// Console Payment Service
export const consolePaymentService = {
  plan: async (
    requestData: ConsolePaymentRequest,
  ): Promise<SubscriptionResponse> => {
    try {
      const response = await axiosInstance.post<SubscriptionResponse>(
        "/console/payment/plan",
        requestData,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(
          error?.response?.data?.message ||
            "Failed to initiate console payment",
        );
      }
      throw new Error(
        "An unexpected error occurred while initiating console payment",
      );
    }
  },
};

// Subscription service
export const subscriptionService = {
  subscribe: async (
    tier: string,
    provider?: string,
    frequency?: string,
    organizationId?: string,
    featureIds?: string[],
    directPlan?: {
      tier: "STARTER" | "GROWTH" | "SCALE";
      includeSetupFee: boolean;
    },
    directFeatureIds?: string[],
    amount?: number,
    lifecyclePhaseIds?: number[],
  ): Promise<SubscriptionResponse> => {
    try {
      const requestBody: {
        tier: string;
        provider?: string;
        frequency?: string;
        organizationId?: string;
        featureIds?: string[];
        directPlan?: {
          tier: "STARTER" | "GROWTH" | "SCALE";
          includeSetupFee: boolean;
        };
        directFeatureIds?: string[];
        amount?: number;
        lifecyclePhaseIds?: number[];
      } = { tier };
      console.log("Request Body:", requestBody);

      if (provider) requestBody.provider = provider;
      if (frequency) requestBody.frequency = frequency;
      if (organizationId) requestBody.organizationId = organizationId;
      requestBody.featureIds = featureIds || [];
      if (directPlan) requestBody.directPlan = directPlan;
      if (directFeatureIds && directFeatureIds.length > 0)
        requestBody.directFeatureIds = directFeatureIds;
      if (amount) requestBody.amount = amount;
      if (lifecyclePhaseIds && lifecyclePhaseIds.length > 0)
        requestBody.lifecyclePhaseIds = lifecyclePhaseIds;

      // Log the complete payload being sent to backend
      console.log("=== SUBSCRIPTION SERVICE PAYLOAD ===");
      console.log("Endpoint: /billing/subscribe");
      console.log("Request Body:", JSON.stringify(requestBody, null, 2));

      const response = await axiosInstance.post<SubscriptionResponse>(
        "/billing/subscribe",
        requestBody,
      );
      toast.success(response?.data?.message);

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error?.response?.data?.message);
        // Handle axios specific errors
        // const errorMessage = error.response?.data?.message || error.message;
        // throw new Error(errorMessage);
      }
      throw new Error("An unexpected error occurred");
    }
  },
};

// Manual transfer service for ai recruiter
export const manualTransferService = {
  prepare: async (
    requestData: PrepareManualTransferRequest & { amount?: number },
  ): Promise<PrepareManualTransferResponse> => {
    try {
      const response = await axiosInstance.post<PrepareManualTransferResponse>(
        "/billing/prepare-manual-transfer",
        requestData,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(
          error?.response?.data?.message || "Failed to prepare manual transfer",
        );
      }
      throw new Error(
        "An unexpected error occurred while preparing manual transfer",
      );
    }
  },

  submitReceipt: async (
    receipt: File,
    organizationId: string,
    tier?: string,
    frequency?: string,
    featureIds?: string[],
    directPlan?: {
      tier: "STARTER" | "GROWTH" | "SCALE";
      includeSetupFee: boolean;
    },
    directFeatureIds?: string[],
    amount?: number,
    reference?: string,
  ): Promise<SubmitPaymentReceiptResponse> => {
    try {
      const formData = new FormData();
      formData.append("receipt", receipt);
      formData.append("organizationId", organizationId);

      if (tier) formData.append("tier", tier);
      if (frequency) formData.append("frequency", frequency);
      formData.append(
        "featureIds",
        featureIds && featureIds.length > 0 ? featureIds.join(",") : "",
      );
      if (directPlan) formData.append("directPlan", JSON.stringify(directPlan));
      formData.append(
        "directFeatureIds",
        directFeatureIds && directFeatureIds.length > 0
          ? directFeatureIds.join(",")
          : "",
      );
      if (amount) formData.append("amount", amount.toString());
      if (reference) formData.append("reference", reference);

      console.log("=== SUBMIT RECEIPT PAYLOAD ===");
      console.log("OrganizationId:", organizationId);
      console.log("Tier:", tier);
      console.log("Frequency:", frequency);
      console.log("Amount:", amount);
      console.log("reference:", reference);
      if (directPlan) console.log("DirectPlan:", JSON.stringify(directPlan));

      const response = await axiosInstance.post<SubmitPaymentReceiptResponse>(
        "/billing/submit-payment-receipt",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      toast.success(response?.data?.message || "Payment receipt submitted");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          error?.response?.data?.message || "Failed to submit payment receipt";
        toast.error(message);
        throw new Error(message);
      }
      throw new Error(
        "An unexpected error occurred while submitting payment receipt",
      );
    }
  },
};

export const getAdmins = async (id: string): Promise<GetAdminsResponse> => {
  const response = await axiosInstance.get(`/organizations/${id}/admins`);
  return response.data;
};

export const getAllAdmins = {
  getAll: async (
    params?: Record<string, any>,
  ): Promise<AdminApiResponse<SuperAdminResponse>> => {
    const response = await axiosInstance.get<
      AdminApiResponse<SuperAdminResponse>
    >("/organizations", { params });
    return response.data;
  },

  getById: async (id: string): Promise<AdminApiResponse<OrgSuperAdmin>> => {
    const response = await axiosInstance.get<AdminApiResponse<OrgSuperAdmin>>(
      `/organizations/${id}`,
    );
    return response.data;
  },
};

export const getOrgById = async (
  id: string,
): Promise<AdminApiResponse<OrgSuperAdmin>> => {
  const response = await axiosInstance.get(`/organizations/${id}`);
  return response.data;
};

export interface VerifyAdminResponse {
  success: boolean;
  message: string;
  data?: any;
}

export const adminService = {
  verifyAdmin: async (
    organizationId: string,
    tier: string,
  ): Promise<VerifyAdminResponse> => {
    const response = await axiosInstance.post(
      `/organizations/${organizationId}/verify`,
      { tier },
    );

    if (response.data?.message) {
      toast.success(response.data.message);
    }

    return response.data;
  },
};
// export const adminService = {
//   verifyAdmin: async (
//     organizationId: string
//     // tier: string
//   ): Promise<VerifyAdminResponse> => {
//     const response = await axiosInstance.post(
//       `/organizations/${organizationId}/verify`
//     );
//     toast.success(response?.data?.message);
//     return response.data;
//   },
// };

// Delete Admin
export const deleteAdmin = async (
  adminId: string,
  dispatch: any,
): Promise<boolean> => {
  dispatch(setLoading());
  try {
    const response = await axiosInstance.delete(`/organizations/${adminId}`);

    if (response.data?.message) {
      toast.success(response.data.message);
    } else {
      toast.success("Organization deleted successfully");
    }

    return true;
  } catch (error) {
    if (isAxiosError(error)) {
      toast.error(
        error?.response?.data?.message || "Failed to delete organization",
      );
    } else {
      toast.error("An unexpected error occurred");
    }
    return false;
  } finally {
    dispatch(stopLoading());
  }
};

export interface InitializeOnboardingPaymentRequest {
  reference: string;
  provider: "paystack" | "paypal" | "manual" | "bank-transfer";
}

export interface InitializeOnboardingPaymentResponse {
  success: boolean;
  data: {
    authorizationUrl?: string;
    reference: string;
    alreadyProcessed: boolean;
  };
}

export const onboardingPaymentService = {
  initialize: async (
    data: InitializeOnboardingPaymentRequest,
  ): Promise<InitializeOnboardingPaymentResponse> => {
    try {
      const payload = {
        reference: data.reference,
        provider: data.provider === "bank-transfer" ? "manual" : data.provider,
      };
      const response =
        await axiosInstance.post<InitializeOnboardingPaymentResponse>(
          "/billing/initialize-onboarding-payment",
          payload,
        );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(
          error?.response?.data?.message || "Failed to initialize payment",
        );
      }
      throw new Error(
        "An unexpected error occurred while initializing onboarding payment",
      );
    }
  },
};

export const adminAddonService = {
  create: async (payload: any): Promise<any> => {
    try {
      const response = await axiosInstance.post(
        "/admin/services/addons",
        payload,
      );
      toast.success(response.data?.message || "Add-on created successfully");
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        toast.error(
          error?.response?.data?.message || "Failed to create add-on",
        );
      }
      throw new Error("An unexpected error occurred while creating add-on");
    }
  },

  update: async (
    id: string,
    payload: any,
    // serviceType: string,
  ): Promise<any> => {
    try {
      // Merge id and serviceType into the body as requested by best practices
      const fullPayload = { ...payload };
      const response = await axiosInstance.patch(
        `/admin/services/addons/${id}`,
        fullPayload,
      );
      toast.success(response.data?.message || "Add-on updated successfully");
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        toast.error(
          error?.response?.data?.message || "Failed to update add-on",
        );
      }
      throw new Error("An unexpected error occurred while updating add-on");
    }
  },

  delete: async (id: string, serviceType: string): Promise<any> => {
    try {
      const response = await axiosInstance.delete(
        `/admin/services/addons/${id}`,
        { params: { serviceType } },
      );
      toast.success(response.data?.message || "Add-on deleted successfully");
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        toast.error(
          error?.response?.data?.message || "Failed to delete add-on",
        );
      }
      throw new Error("An unexpected error occurred while deleting add-on");
    }
  },
};

export const adminPlanService = {
  create: async (payload: any): Promise<any> => {
    try {
      const response = await axiosInstance.post(
        "/admin/services/plans",
        payload,
      );
      toast.success(response.data?.message || "Plan created successfully");
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        toast.error(error?.response?.data?.message || "Failed to create plan");
      }
      throw new Error("An unexpected error occurred while creating plan");
    }
  },

  update: async (id: string, payload: any): Promise<any> => {
    try {
      const response = await axiosInstance.patch(
        `/admin/services/plans/${id}`,
        payload,
      );
      toast.success(response.data?.message || "Plan updated successfully");
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        toast.error(error?.response?.data?.message || "Failed to update plan");
      }
      throw new Error("An unexpected error occurred while updating plan");
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await axiosInstance.delete(
        `/admin/services/plans/${id}`,
      );
      toast.success(response.data?.message || "Plan deleted successfully");
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        toast.error(error?.response?.data?.message || "Failed to delete plan");
      }
      throw new Error("An unexpected error occurred while deleting plan");
    }
  },
};

export const adminLifecycleService = {
  phases: {
    create: async (payload: any): Promise<any> => {
      try {
        const response = await axiosInstance.post(
          "/admin/lifecycle/phases",
          payload,
        );
        toast.success(
          response.data?.message || "Lifecycle Phase created successfully",
        );
        return response.data;
      } catch (error) {
        if (isAxiosError(error))
          toast.error(
            error?.response?.data?.message || "Failed to create phase",
          );
        throw new Error("An unexpected error occurred while creating phase");
      }
    },
    update: async (id: number | string, payload: any): Promise<any> => {
      try {
        const response = await axiosInstance.patch(
          `/admin/lifecycle/phases/${id}`,
          payload,
        );
        toast.success(
          response.data?.message || "Lifecycle Phase updated successfully",
        );
        return response.data;
      } catch (error) {
        if (isAxiosError(error))
          toast.error(
            error?.response?.data?.message || "Failed to update phase",
          );
        throw new Error("An unexpected error occurred while updating phase");
      }
    },
    delete: async (id: number | string): Promise<any> => {
      try {
        const response = await axiosInstance.delete(
          `/admin/lifecycle/phases/${id}`,
        );
        toast.success(
          response.data?.message || "Lifecycle Phase deleted successfully",
        );
        return response.data;
      } catch (error) {
        if (isAxiosError(error))
          toast.error(
            error?.response?.data?.message || "Failed to delete phase",
          );
        throw new Error("An unexpected error occurred while deleting phase");
      }
    },
  },
  pepmTiers: {
    create: async (payload: any): Promise<any> => {
      try {
        const response = await axiosInstance.post(
          "/admin/lifecycle/pepm-tiers",
          payload,
        );
        toast.success(
          response.data?.message || "PEPM Tier created successfully",
        );
        return response.data;
      } catch (error) {
        if (isAxiosError(error))
          toast.error(
            error?.response?.data?.message || "Failed to create PEPM tier",
          );
        throw new Error(
          "An unexpected error occurred while creating PEPM tier",
        );
      }
    },
    update: async (id: number | string, payload: any): Promise<any> => {
      try {
        const response = await axiosInstance.patch(
          `/admin/lifecycle/pepm-tiers/${id}`,
          payload,
        );
        toast.success(
          response.data?.message || "PEPM Tier updated successfully",
        );
        return response.data;
      } catch (error) {
        if (isAxiosError(error))
          toast.error(
            error?.response?.data?.message || "Failed to update PEPM tier",
          );
        throw new Error(
          "An unexpected error occurred while updating PEPM tier",
        );
      }
    },
    delete: async (id: number | string): Promise<any> => {
      try {
        const response = await axiosInstance.delete(
          `/admin/lifecycle/pepm-tiers/${id}`,
        );
        toast.success(
          response.data?.message || "PEPM Tier deleted successfully",
        );
        return response.data;
      } catch (error) {
        if (isAxiosError(error))
          toast.error(
            error?.response?.data?.message || "Failed to delete PEPM tier",
          );
        throw new Error(
          "An unexpected error occurred while deleting PEPM tier",
        );
      }
    },
  },
  volumeDiscounts: {
    create: async (payload: any): Promise<any> => {
      try {
        const response = await axiosInstance.post(
          "/admin/lifecycle/volume-discounts",
          payload,
        );
        toast.success(
          response.data?.message || "Volume Discount created successfully",
        );
        return response.data;
      } catch (error) {
        if (isAxiosError(error))
          toast.error(
            error?.response?.data?.message ||
              "Failed to create volume discount",
          );
        throw new Error(
          "An unexpected error occurred while creating volume discount",
        );
      }
    },
    update: async (id: number | string, payload: any): Promise<any> => {
      try {
        const response = await axiosInstance.patch(
          `/admin/lifecycle/volume-discounts/${id}`,
          payload,
        );
        toast.success(
          response.data?.message || "Volume Discount updated successfully",
        );
        return response.data;
      } catch (error) {
        if (isAxiosError(error))
          toast.error(
            error?.response?.data?.message ||
              "Failed to update volume discount",
          );
        throw new Error(
          "An unexpected error occurred while updating volume discount",
        );
      }
    },
    delete: async (id: number | string): Promise<any> => {
      try {
        const response = await axiosInstance.delete(
          `/admin/lifecycle/volume-discounts/${id}`,
        );
        toast.success(
          response.data?.message || "Volume Discount deleted successfully",
        );
        return response.data;
      } catch (error) {
        if (isAxiosError(error))
          toast.error(
            error?.response?.data?.message ||
              "Failed to delete volume discount",
          );
        throw new Error(
          "An unexpected error occurred while deleting volume discount",
        );
      }
    },
  },
};

export const offeringsService = {
  getAll: async (): Promise<OfferingsResponse> => {
    try {
      const response = await axiosInstance.get<OfferingsResponse>(
        "/admin/services/offerings",
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(
          error?.response?.data?.message || "Failed to fetch offerings",
        );
      }
      throw new Error("An unexpected error occurred while fetching offerings");
    }
  },
};
